import React, { Suspense, useEffect, useState } from 'react';
import Media from 'react-media';
import { history } from '../../utils/history';
import OverlayDialog from '../../app/common/components/OverlayDialog';
import { SupportedLanguage } from '../../config/constants/supportedLanguage.enum';
import { PageId } from '../../config/constants/pageId.enum';
import OverlayHelper from '../../utils/overlayHelper';
import { FormattedMessage } from 'react-intl';
import { ServiceLinkButton } from './ServiceLinkButton';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import EkycServiceContainer from './unlinkMyNumber/EkycServiceContainer';
import {
    Base,
    ServiceWrapper,
    Header,
    Description,
    ServiceLinkWrapper,
    ServiceLinkListWrapper,
    Info,
    InfoList,
    Spacer,
} from './IdentityVerificationStyled';
import { ILinkedService } from './IdentityVerificationInterface';
import {
    startChallenge,
    destroyChallenge,
    updateChallengerLanguage,
} from '../../utils/challengerMain';
import { Link } from './IdentityVerificationStyled';
import { MQS } from '../../app/common/constants/styles';
//Lazy load the HeadTitle component to solve the circular dependency issue
const HeadTitle = React.lazy(() => import('../../app/common/components/HeadTitle'));

enum IdentityVerificationFormType {
    RakutenPayOverlay = 'RakutenPayOverlay',
    // Add another service type here in the future
}

interface IdentityVerificationProps {
    member: any;
    isOverlayOpen: boolean;
    isReady: boolean;
    locale: SupportedLanguage;
    challengerPid: string;
    closeIdentityVerificationOverlay: () => any;
    openIdentityVerificationOverlay: () => any;
    getLinkedServices: () => void;
    linkedServices: ILinkedService[];
    loadingLinkedServices: boolean;
    ekycHelpPageUrl: { mobile: string; desktop: string };
}
export const IdentityVerification: React.FC<IdentityVerificationProps> = ({
    isOverlayOpen,
    locale,
    closeIdentityVerificationOverlay,
    openIdentityVerificationOverlay,
    getLinkedServices,
    linkedServices,
    challengerPid,
    ekycHelpPageUrl,
    loadingLinkedServices,
}) => {
    const [overlayTitle, setOverlayTitle] = useState<FormattedMessage.MessageDescriptor>(
        TranslationHelpers.getMessageDescriptor('ekyc.title'),
    );
    const [overlayBodyContent, setOverlayBodyContent] = useState<React.ReactNode>(null);
    useEffect(() => {
        window.scrollTo({ top: 0 });
        startChallenge(challengerPid);

        return () => {
            destroyChallenge(challengerPid);
            OverlayHelper.closeHelper(PageId.ekyc);
        };
    }, [challengerPid]);

    useEffect(() => {
        updateChallengerLanguage(locale);
    }, [locale]);

    useEffect(() => {
        getLinkedServices();
    }, [getLinkedServices]);

    useEffect(() => {
        if (!loadingLinkedServices && linkedServices.length === 0) {
            history.push('/');
        }
    }, [linkedServices, loadingLinkedServices]);

    const openOverlay = (content: IdentityVerificationFormType, clientId: string) => {
        switch (content) {
            case IdentityVerificationFormType.RakutenPayOverlay: //Create an extra case if you add new service
                setOverlayTitle(TranslationHelpers.getMessageDescriptor('ekyc.popup.title'));
                setOverlayBodyContent(
                    <EkycServiceContainer onCloseFunction={closeOverlay} clientId={clientId} />,
                );
                break;

            default:
                setOverlayTitle(TranslationHelpers.getMessageDescriptor('ekyc.title'));
                setOverlayBodyContent(null);
        }
        openIdentityVerificationOverlay();
    };

    const closeOverlay = () => {
        closeIdentityVerificationOverlay();
    };

    return (
        <Base>
            <ServiceWrapper>
                <Suspense fallback={<div>Loading...</div>}>
                    <HeadTitle />
                </Suspense>
                <Description>
                    {TranslationHelpers.getFormattedMessage('ekyc.top.description')}
                    <Spacer />
                    {TranslationHelpers.getFormattedMessage('ekyc.top.description2')}
                </Description>
                {isOverlayOpen && (
                    <OverlayDialog
                        title={overlayTitle}
                        isOpen={isOverlayOpen}
                        onClose={closeOverlay}
                        closeOnOutside={true}
                    >
                        {overlayBodyContent}
                    </OverlayDialog>
                )}
                <ServiceLinkListWrapper>
                    {linkedServices.length !== 0 &&
                        linkedServices.map((service) => {
                            return (
                                <ServiceLinkWrapper key={service.clientId}>
                                    <ServiceLinkButton
                                        onClick={() =>
                                            openOverlay(
                                                IdentityVerificationFormType.RakutenPayOverlay,
                                                service.clientId,
                                            )
                                        }
                                        clientId={service.clientId}
                                    />
                                </ServiceLinkWrapper>
                            );
                        })}
                </ServiceLinkListWrapper>
            </ServiceWrapper>
            <Info>
                {TranslationHelpers.getFormattedMessage('ekyc.info.title')}
                <InfoList>
                    <li>{TranslationHelpers.getFormattedMessage('ekyc.mynumber.info')}</li>
                    <li>{TranslationHelpers.getFormattedMessage('ekyc.unlink.info')}</li>
                    <li>{TranslationHelpers.getFormattedMessage('ekyc.service.info')}</li>
                    <li>
                        {TranslationHelpers.getFormattedMessage('ekyc.help.info')}&nbsp;
                        <Media
                            query={`(max-width: ${MQS.mq2Max})`}
                            render={() => (
                                <Link
                                    href={ekycHelpPageUrl?.mobile}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    target="_blank"
                                >
                                    {TranslationHelpers.getFormattedMessage('ekyc.help.link')}
                                </Link>
                            )}
                        />
                        <Media
                            query={`(min-width: ${MQS.mq3Min})`}
                            render={() => (
                                <Link
                                    href={ekycHelpPageUrl?.desktop}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    target="_blank"
                                >
                                    {TranslationHelpers.getFormattedMessage('ekyc.help.link')}
                                </Link>
                            )}
                        />
                        {TranslationHelpers.getFormattedMessage('ekyc.help.warning')}
                    </li>
                </InfoList>
            </Info>
        </Base>
    );
};
